import React from "react";
import {
  Shield,
  Bell,
  Ban,
  UserX,
  UserCheck,
  Send,
  AlertTriangle,
  Loader2,
  Sparkles,
} from "lucide-react";
import { UserData } from "../../../lib/types/authOnboarding";

interface AdminActionsCardProps {
  user: UserData;
  profileCompletion: number;
  isProfileIncomplete: boolean;
  isSendingReminder: boolean;
  isSendingTaskReminder: boolean;
  isReactivating: boolean;
  onSendProfileReminder: () => void;
  onSendTaskReminder: () => void;
  onReactivate: () => void;
  onShowSuspendModal: () => void;
  onShowTerminateModal: () => void;
}

const AdminActionsCard: React.FC<AdminActionsCardProps> = ({
  user,
  profileCompletion,
  isProfileIncomplete,
  isSendingReminder,
  isSendingTaskReminder,
  isReactivating,
  onSendProfileReminder,
  onSendTaskReminder,
  onReactivate,
  onShowSuspendModal,
  onShowTerminateModal,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Admin Actions</h2>
          <p className="text-sm text-gray-500">
            Manage user account and send notifications
          </p>
        </div>
      </div>

      {/* Profile Incomplete Warning */}
      {isProfileIncomplete && user.status === "active" && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800">
                Incomplete Profile
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                This user has only completed {profileCompletion} of 4
                verification steps. Send a reminder to encourage them to
                complete their profile.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Suspended Status Warning */}
      {user.status === "suspended" && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Ban className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-orange-800">
                Account Suspended
              </h4>
              <p className="text-sm text-orange-700 mt-1">
                This user's account is currently suspended. They cannot access
                projects or submit work until reactivated.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Terminated Status Warning */}
      {user.status === "terminated" && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-start gap-3">
            <UserX className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800">
                Account Terminated
              </h4>
              <p className="text-sm text-red-700 mt-1">
                This user's account has been permanently terminated. They no
                longer have access to the platform.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Send Profile Reminder Button */}
        {isProfileIncomplete && user.status === "active" && (
          <button
            onClick={onSendProfileReminder}
            disabled={isSendingReminder}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              {isSendingReminder ? (
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              ) : (
                <Bell className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">
                Profile Reminder
              </p>
              <p className="text-xs text-gray-500">Complete profile</p>
            </div>
          </button>
        )}

        {/* Send Task Reminder Button (for active users) */}
        {user.status === "active" && (
          <button
            onClick={onSendTaskReminder}
            disabled={isSendingTaskReminder}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
              {isSendingTaskReminder ? (
                <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">
                Task Reminder
              </p>
              <p className="text-xs text-gray-500">New tasks available</p>
            </div>
          </button>
        )}

        {/* Reactivate Button (for suspended users) */}
        {user.status === "suspended" && (
          <button
            onClick={onReactivate}
            disabled={isReactivating}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
              {isReactivating ? (
                <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
              ) : (
                <UserCheck className="w-5 h-5 text-emerald-600" />
              )}
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">Reactivate</p>
              <p className="text-xs text-gray-500">Restore access</p>
            </div>
          </button>
        )}

        {/* Suspend Button */}
        {user.status === "active" && (
          <button
            onClick={onShowSuspendModal}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl hover:border-orange-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
              <Ban className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">
                Suspend User
              </p>
              <p className="text-xs text-gray-500">Temporary block</p>
            </div>
          </button>
        )}

        {/* Terminate Button */}
        {user.status !== "terminated" && (
          <button
            onClick={onShowTerminateModal}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-xl hover:border-red-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">
                Terminate User
              </p>
              <p className="text-xs text-gray-500">Permanent removal</p>
            </div>
          </button>
        )}

        {/* Send Email Button (always visible for active users) */}
        {user.status === "active" && (
          <a
            href={`mailto:${user.email}`}
            className="flex items-center gap-3 p-4 bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl hover:border-purple-300 hover:shadow-md transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <Send className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-gray-900 text-sm">Send Email</p>
              <p className="text-xs text-gray-500">Direct contact</p>
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

export default AdminActionsCard;
